/**
 * Auth interceptor — global 401 handler with single-flight refresh.
 *
 * URGENT directive (May 5 2026): "If auth fails, show a clear
 * 'session expired — log in again' screen, not a dead-end toast."
 *
 * P0 May 22 2026 — explicit 5xx/network guard added.
 *
 * P0 Feb 28 2026 — root-cause fix for "Login loop after preview
 *   resume" reproduced via headless trace + Set-Cookie audit.
 *   Cause: `access_token` cookie has `Max-Age=900s` (15 min) but
 *   `refresh_token` lives 1 week. With no client-side refresh,
 *   the very first API call after an idle pause (preview resume,
 *   coffee break, app-switch on iPhone) 401'd → hard redirect to
 *   /login → re-login → 15 min later, same loop.
 *   Fix: on any non-/api/auth/refresh 401, attempt ONE
 *   single-flight POST /api/auth/refresh. If 200, transparently
 *   re-issue the failed request with `_retried_after_refresh`
 *   guard so a second 401 falls through to the existing
 *   /login redirect (no infinite loop).
 *
 * Behaviour:
 *   • Catches every axios 401 response.
 *   • 5xx / network / Cloudflare 52x are NEVER treated as auth
 *     failures — they pass through untouched so callers (e.g. the
 *     upload pipeline's retry-with-backoff) can react.
 *   • If user is on a public route (/login, /signup, /forgot-password,
 *     /reset-password) → pass through (let the page show its own
 *     error UI; we don't want a redirect loop).
 *   • Otherwise on 401:
 *       (a) If this IS /api/auth/refresh itself → skip refresh
 *           attempt; refresh-token is dead, fall through to /login.
 *       (b) Else attempt single-flight refresh. If success →
 *           retry the original request once (no further refresh
 *           attempts thanks to `_retried_after_refresh` marker).
 *       (c) If refresh fails → clear any cached auth tokens,
 *           remember the original destination, hard-redirect to
 *           /login?session_expired=1.
 *   • Login page reads the flag and shows a calm "Your session expired"
 *     banner.
 *
 * Re-entrancy guard: `redirected` flag prevents N concurrent
 * 401s from firing N redirects. `refreshInFlight` collapses N
 * parallel refresh attempts into a single backend call.
 *
 * Lifecycle logs:
 *   • interceptor_passthrough_5xx — 5xx propagated to caller
 *   • interceptor_passthrough_public — 401 on public route
 *   • interceptor_refresh_attempt — single-flight refresh started
 *   • interceptor_refresh_success — refresh + original-request retry succeeded
 *   • interceptor_refresh_failed — refresh returned non-200 or threw
 *   • interceptor_redirect_reason — exactly what triggered the redirect
 *   • interceptor_suppressed_parallel_401 — parallel 401 arrived after
 *     the first one already armed the redirect (forensic counter only;
 *     does NOT change behaviour or fire a second redirect)
 *   • auth_reset_triggered — final redirect signal for the diag rail
 */
import axios from "axios";

const PUBLIC_PATHS = [
  "/login", "/signup", "/forgot-password",
  "/reset-password", "/private",
  // QA scaffolding routes — `/test/*` mounts the Mobile Shell Phase 1
  // demo page. Must never trigger a session-expired redirect because
  // the page is intentionally open for Playwright regression.
  "/test",
  // Design-pass static mockup gallery — also intentionally open.
  // Removed in implementation sprint cleanup commit (Feb 18, 2026).
  "/_mockup",
  // Diagnostic — pure forensic iPhone file-input probe. No auth,
  // no API calls. Operator loads it directly on the iPhone Photos
  // picker boundary; a session-expired redirect would break the
  // diagnostic. See /app/frontend/src/pages/IntakeProbe.jsx.
  "/intake-probe",
  // P0 fix (Feb 2026) — `/invite/:code` is public by design: the
  // whole point of an invite link is that an UNAUTHENTICATED learner
  // taps it on a fresh device. Without this entry the auth bootstrap
  // 401 → interceptor redirects them to `/login?session_expired=1`
  // before they ever reach the PIN/onboarding form. Doctrine-locked
  // by `tests/test_public_paths_invite_register_doctrine.py`.
  "/invite",
  // P0 fix (Feb 2026) — `/register` is wrapped in <PublicRoute> in
  // App.js for the same reason: brand-new users have no session.
  // Doctrine-locked alongside `/invite`.
  "/register",
];

function isPublicRoute() {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname;
  return PUBLIC_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
}

function logInterceptor(event, payload) {
  // eslint-disable-next-line no-console
  try { console.info("[auth-interceptor]", event, payload || {}); }
  catch (_) { /* noop */ }
}

// Derive the API origin from a failing-request URL so the refresh
// call lands on the same backend. Falls back to window.location.origin
// when the failing URL was a relative path.
function deriveApiBase(failingUrl) {
  if (typeof failingUrl === "string") {
    const idx = failingUrl.indexOf("/api/");
    if (idx > 0) return failingUrl.slice(0, idx);
  }
  if (typeof window !== "undefined") return window.location.origin;
  return "";
}

let redirected = false;
// Forensic counter — number of parallel 401s that arrived AFTER
// the first 401 had already armed the redirect. We never act on
// these (behaviour is identical: reject the promise), but we DO
// log a distinct `interceptor_suppressed_parallel_401` event so
// post-mortem DevTools / log capture can answer the question
// "how many simultaneous things 401'd when the user got
// bounced?" without N copies of `interceptor_redirect_reason`
// polluting the trail. Reset to 0 on every fresh page load
// (because `redirected` resets on hard navigation).
let suppressedParallel401Count = 0;

// Single-flight refresh state.
// `refreshInFlight` is the in-progress refresh promise (or null);
// N parallel 401s all `await` the same promise so the backend
// only sees ONE POST /api/auth/refresh per burst.
// `refreshDisabledForSession` latches true after a failed refresh
// so we don't beat on a dead refresh_token over and over inside
// the same page lifetime — that would create exactly the loop
// this whole batch is trying to kill.
let refreshInFlight = null;
let refreshDisabledForSession = false;

async function attemptRefresh(apiBase) {
  if (refreshDisabledForSession) return false;
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    logInterceptor("interceptor_refresh_attempt", { base: apiBase });
    try {
      const res = await axios.post(
        `${apiBase}/api/auth/refresh`,
        null,
        { withCredentials: true },
      );
      const ok = res?.status === 200;
      if (!ok) {
        refreshDisabledForSession = true;
        logInterceptor("interceptor_refresh_failed", {
          status: res?.status, reason: "non_200",
        });
      } else {
        logInterceptor("interceptor_refresh_success", {});
      }
      return ok;
    } catch (e) {
      refreshDisabledForSession = true;
      logInterceptor("interceptor_refresh_failed", {
        status: e?.response?.status ?? 0,
        reason: e?.response?.status === 401 ? "refresh_token_invalid" : "exception",
      });
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export function installAuthInterceptor() {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      // Only handle real 401s — let other errors propagate so callers
      // can react (validation, permission, payment, transient 5xx).
      if (status !== 401) {
        if (status >= 500 && status <= 599) {
          logInterceptor("interceptor_passthrough_5xx", {
            status,
            url: error?.config?.url,
            reason: "5xx is transient infra, NOT auth failure",
          });
        }
        return Promise.reject(error);
      }

      const failingUrl = error?.config?.url || "unknown";
      const failingMethod = (error?.config?.method || "GET").toUpperCase();
      const isRefreshEndpoint =
        typeof failingUrl === "string" &&
        failingUrl.endsWith("/api/auth/refresh");
      const alreadyRetried = !!error?.config?._retried_after_refresh;

      // Try the single-flight refresh before deciding to redirect.
      // Skip when:
      //   • The failing call IS /api/auth/refresh (would loop).
      //   • We already retried this same request after a refresh
      //     (the retry came back 401 → refresh didn't actually
      //     restore auth; bail to /login).
      //   • Refresh has been disabled for this page lifetime
      //     (one failed attempt is enough).
      //   • The user is on a public route (no point refreshing).
      if (
        !isRefreshEndpoint
        && !alreadyRetried
        && !refreshDisabledForSession
        && !isPublicRoute()
      ) {
        const apiBase = deriveApiBase(failingUrl);
        const refreshed = await attemptRefresh(apiBase);
        if (refreshed) {
          // Mark the original config so the interceptor cannot
          // try to refresh again on this same request (defends
          // against a backend that 401s even after a fresh
          // token, e.g. role-revoked mid-session).
          const retryConfig = { ...error.config, _retried_after_refresh: true };
          return axios.request(retryConfig);
        }
      }

      if (redirected) {
        // Parallel 401 — the first 401 already armed the redirect
        // and wrote sessionStorage.auth_reset_reason. Behaviour is
        // identical to before this counter existed; the only new
        // thing is the forensic log so the operator can see "1
        // primary 401 + N suppressed parallel 401s" in DevTools.
        suppressedParallel401Count += 1;
        logInterceptor("interceptor_suppressed_parallel_401", {
          suppressed_count: suppressedParallel401Count,
          url: failingUrl,
          method: failingMethod,
        });
        return Promise.reject(error);
      }
      if (isPublicRoute()) {
        logInterceptor("interceptor_passthrough_public", {
          path: window.location.pathname,
        });
        return Promise.reject(error);
      }

      redirected = true;
      logInterceptor("interceptor_redirect_reason", {
        status: 401,
        url: failingUrl,
        method: failingMethod,
        from: window.location.pathname,
      });
      logInterceptor("auth_reset_triggered", {
        target: "/login?session_expired=1",
      });
      try {
        // Persist where the user wanted to go so we can offer a
        // "back to where you were" CTA after re-login if you want
        // to wire it later.
        sessionStorage.setItem(
          "auth_return_to",
          window.location.pathname + window.location.search
        );
        // Persist the exact failing endpoint + status so the Login
        // page (and any future diagnostic UI) can surface a clear
        // "why did I get bounced?" message instead of a generic
        // "Your session expired" banner. P0 Feb 28, 2026 — operator
        // brief: "log the exact failing endpoint/status".
        sessionStorage.setItem(
          "auth_reset_reason",
          JSON.stringify({
            url: failingUrl,
            method: failingMethod,
            status: 401,
            from: window.location.pathname,
            at: Date.now(),
          })
        );
      } catch { /* ignore */ }
      // Hard navigate so React state is reset cleanly.
      window.location.href = "/login?session_expired=1";
      return Promise.reject(error);
    }
  );
}
